// Centralized settings management utility
// All app settings are now stored in IndexedDB for better persistence

import { IndexedDBService } from "../services/indexeddb.service";

export interface AppSettings {
  theme?: string;
  rememberMe?: boolean;
  isAuthenticated?: boolean;
  rememberedEmail?: string;
  uploadTrial?: any[];
  routes?: any;
  expandableOtherTracks?: any;
  metadata?: any;
  [key: string]: any; // Allow dynamic keys
}

export class Settings {
  private static indexedDB: IndexedDBService | null = null;

  // Initialize the IndexedDB service
  static async init(indexedDB: IndexedDBService): Promise<void> {
    this.indexedDB = indexedDB;
  }

  // Get all settings from IndexedDB
  static async getAll(): Promise<AppSettings> {
    if (!this.indexedDB) {
      throw new Error(
        "Settings not initialized. Call Settings.init() in app.ts first.",
      );
    }

    const allItems = await this.indexedDB.getAllItems("settings");
    const settings: AppSettings = {};
    allItems.forEach((item) => {
      settings[item.key] = item.value;
    });
    return settings;
  }

  // Get a specific setting from IndexedDB
  static async get<T>(key: string): Promise<T | null> {
    if (!this.indexedDB) {
      throw new Error(
        "Settings not initialized. Call Settings.init() in app.ts first.",
      );
    }

    const value = await this.indexedDB.getItem("settings", key);
    return value !== undefined ? value : null;
  }

  // Set a specific setting in IndexedDB
  static async set(key: string, value: any): Promise<void> {
    if (!this.indexedDB) {
      throw new Error(
        "Settings not initialized. Call Settings.init() in app.ts first.",
      );
    }

    await this.indexedDB.setItem("settings", key, value);
  }

  // Remove a specific setting from IndexedDB
  static async remove(key: string): Promise<void> {
    if (!this.indexedDB) {
      throw new Error(
        "Settings not initialized. Call Settings.init() in app.ts first.",
      );
    }

    await this.indexedDB.removeItem("settings", key);
  }

  // Clear all settings from IndexedDB
  static async clear(): Promise<void> {
    if (!this.indexedDB) {
      throw new Error(
        "Settings not initialized. Call Settings.init() in app.ts first.",
      );
    }

    await this.indexedDB.clear("settings");
  }

  // Check if a setting exists in IndexedDB
  static async has(key: string): Promise<boolean> {
    if (!this.indexedDB) {
      throw new Error(
        "Settings not initialized. Call Settings.init() in app.ts first.",
      );
    }

    const value = await this.indexedDB.getItem("settings", key);
    return value !== undefined;
  }

  // Synchronous versions for backward compatibility (uses localStorage)
  static getAllSync(): AppSettings {
    const settings = localStorage.getItem("settings");
    return settings ? JSON.parse(settings) : {};
  }

  static getSync<T>(key: string): T | null {
    const settings = this.getAllSync();
    return settings[key] !== undefined ? settings[key] : null;
  }

  static setSync(key: string, value: any): void {
    const settings = this.getAllSync();
    settings[key] = value;
    localStorage.setItem("settings", JSON.stringify(settings));
  }

  static removeSync(key: string): void {
    const settings = this.getAllSync();
    delete settings[key];
    localStorage.setItem("settings", JSON.stringify(settings));
  }
}
